import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../models/statuses";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import JobSubStatusModal from "./StatusesModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const StatusesPage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    statuses,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createStatus,
    updateStatus,
  } = useActions();

  const [selectedStatus, setSelectedStatus] = useState<Statuses>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status.toLowerCase() === Status.ACTIVE.toLowerCase() ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const editJobSubStatus = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findJobSubStatus = statuses.find((status) => Number(status.id) === Number(index));

    if (findJobSubStatus) {
      setSelectedStatus(findJobSubStatus);
      setOpenModal(true);
    }
  };

  const changeStatusJobSubStatus = (index: number) => {
    const findJobSubStatus = statuses.find((status) => Number(status.id) === Number(index));

    if (findJobSubStatus) {
      updateStatus({
        id: Number(index),
        status: {
          ...findJobSubStatus,
          status: findJobSubStatus.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          data-cy="changeStatus"
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }

    return null;
  };

  const handleSaveJobSubStatusSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, statuses);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    {
      name: "name",
      title: translate("name"),
    },
    {
      name: "usedBy",
      searchIdentifier: "used_by",
      title: translate("usedBy"),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission?.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setSelectedStatus(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("name"),
          column: "name",
        },
        {
          name: translate("usedBy"),
          column: "used_by",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.statuses",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="jobSubStatusDataList">
      <ShipeeziNewGrid
        data={statuses}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission?.edit)
            ? (jobSubStatusId) => editJobSubStatus(Number(jobSubStatusId))
            : undefined,
          handleChange: (jobSubStatusId) => changeStatusJobSubStatus(Number(jobSubStatusId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveJobSubStatusSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <JobSubStatusModal
          status={selectedStatus}
          open={openModal}
          hasError={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          updateOrCreateStatus={(status: Statuses) =>
            selectedStatus
              ? selectedStatus.id &&
                updateStatus({
                  id: selectedStatus.id,
                  status: { ...selectedStatus, ...status },
                })
              : createStatus({
                  ...status,
                  companyId: currentUser?.companyId,
                  valid: true,
                } as Statuses)
          }
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateStatus({
                id: rowId,
                status: { status } as Statuses,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default StatusesPage;
